import { useState, useContext, useEffect } from "react";
// @mui material components
import Card from "@mui/material/Card";
import { useNavigate } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import Swal from "sweetalert2";

import { findReceiptFromAccountingSession } from "../../../Classes/API/ReceiptsApi";
import AccountingApi from "../../../Classes/API/AccountingApi";
import PayFromBalanceReq from "../../../Classes/Models/Requests/Accounting/PayFromBalanceReq";
import SessionsApi from "../../../Classes/API/Administration/SessionsApi";
import SearchStringOrIntReq from "../../../Classes/Models/Requests/SearchStringOrIntReq";
import PayFromBalanceButton from "../PayFromBalanceButton";
import AddmissionButton from "../AddmissionButton";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { LanguageContext } from "App";
import GenerateReceiptButton from "CinicComponents/GenerateReceiptButton";
import { useTenant } from "../../../context/TenantContext";

const AccSessionDetailsCard = ({ CurrentUser, Session, setIsLoaded }) => {
  const [receipt, setReceipt] = useState();

  // Use the tenant hook
  const { currentTenant } = useTenant();
  const idValue = currentTenant?.id;

  useEffect(() => {
    findReceiptFromAccountingSession(Session.id)
      .then((res) => setReceipt(res.id))
      .catch((err) => {});
  }, [Session.id]);

  const navigate = useNavigate();

  const CreateAppointment = () => {
    navigate("/Appointments/", {
      state: {
        AccSession: Session,
      },
    });
  };

  const MakeEntry = () => {
    navigate("/CreateNewAccountingEntry", {
      state: {
        AccSession: Session,
        entryType: "AccountingSession",
      },
    });
  };

  const GetPaymentButton = () => {
    if (!Session.isPaidFor) {
      if (Session.packageId) {
        return <PayFromBalanceButton Session={Session} />;
      } else if (Session.domesticStayId) {
        return (
          <span className="badge badge-danger">
            Not Applicable For In Patient
          </span>
        );
      } else {
        return (
          <>
            {(["Admin", "Owner", "HeadAccounting"].includes(
              CurrentUser.userType,
            ) ||
              CurrentUser.userType === "AccountingEmployee" ||
              CurrentUser.userType === "SubAdmin") && (
              <button
                className="btn btn-primary"
                disabled={Session.isPaidFor}
                onClick={() => MakeEntry()}
              >
                CREATE ENTRY
              </button>
            )}
            <PayFromBalanceButton Session={Session} Package={Session.getPac} />
          </>
        );
      }
    } else {
      return <span className="badge badge-success">PAID</span>;
    }
  };

  const redirectLink = `/Appointments/FeedbackForm/${idValue}`;
  const ToggleAbsentButtonClick = async () => {
    if (
      Session.isAbsent &&
      !["Admin", "Owner", "HeadAccounting"].includes(CurrentUser.userType)
    ) {
      Swal.fire(
        "Failed",
        "You are not authorized to perform this action",
        "error",
      );
      return;
    }
    const req = new SearchStringOrIntReq();
    req.SearchInt = Session.id;

    const res = await SessionsApi.ToggleAccountingSessionIsAbsent(req);

    if (res.status === "Success") {
      Swal.fire("Success", res.message, "success").then(() => {
        window.location.reload();
      });
    } else {
      Swal.fire("Failed", res.message, "error");
    }
  };

  const ToggleExceptionButtonClick = async () => {
    // NEW LOGIC: Paid sessions should not be allowed to be marked as exceptions
    if (Session.isPaidFor) {
      Swal.fire(
        "Action Blocked",
        "Paid sessions cannot be marked as exceptions",
        "warning",
      );
      return;
    }

    if (!["Admin", "Owner", "HeadAccounting"].includes(CurrentUser.userType)) {
      Swal.fire(
        "Failed",
        "You are not authorized to perform this action",
        "error",
      );
      return;
    }
    const req = new SearchStringOrIntReq();
    req.SearchInt = Session.id;

    const res = await SessionsApi.ToggleAccountingSessionIsExcteption(req);

    if (res.status === "Success") {
      Swal.fire("Success", res.message, "success").then(() => {
        window.location.reload();
      });
    } else {
      Swal.fire("Failed", res.message, "error");
    }
  };

  const GetAppointmentData = () => {
    if (Session.getConfirmentAppointmentHour) {
      return (
        <>
          <MDTypography variant="h6" className="text-center">
            {Session.getConfirmentAppointmentHour.appointmentHourTime.replace(
              "T",
              "--",
            )}
          </MDTypography>
        </>
      );
    } else if (Session.getFirstConfirmedAppointmentHour) {
      return (
        <>
          <MDTypography variant="h6" className="text-center">
            {Session.getFirstConfirmedAppointmentHour.appointmentHourTime.replace(
              "T",
              "--",
            )}
            , (1/2)
          </MDTypography>
        </>
      );
    } else if (Session.getSecondConfirmedAppointmentHour) {
      return (
        <>
          <MDTypography variant="h6" className="text-center">
            {Session.getSecondConfirmedAppointmentHour.appointmentHourTime.replace(
              "T",
              "--",
            )}
            (1/2)
          </MDTypography>
        </>
      );
    } else {
      return (
        <>
          <MDButton onClick={() => CreateAppointment()}>
            <Icon>add_circle</Icon>
            Create Appointment
          </MDButton>
        </>
      );
    }
  };
  const { translations } = useContext(LanguageContext);
  return (
    <Card sx={{ height: "100%", boxShadow: !false && "none" }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
        px={2}
      >
        <MDTypography
          variant="h6"
          fontWeight="medium"
          textTransform="capitalize"
        >
          {translations.AccountingSessionInfo}:
        </MDTypography>
      </MDBox>
      <MDBox p={2}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoSessionId}: &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;{Session.id}
              </MDTypography>
            </div>
            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoPatientName}: &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;{Session.getPatient.getUser.name}
              </MDTypography>
            </div>
            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Therapist Name: &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;
                {Session.therapistName ||
                  Session.getTherapist?.name ||
                  "Not Assigned"}
              </MDTypography>
            </div>
            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoPatientCurrentBalance}:
                &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;
                {Session.getPatient.getUser.getLastBalanceEntry
                  ? "$" +
                    Session.getPatient.getUser.getLastBalanceEntry
                      .currentBalance
                  : "NO BALANCE AVAILABLE"}
              </MDTypography>
            </div>
            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoSessionName}: &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;{Session.getSession.name}
              </MDTypography>
            </div>
            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoAppointmentTime}: &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;
                {Session.appointmentTime
                  ? new Date(Session.appointmentTime).toLocaleString()
                  : "No appointment"}
              </MDTypography>
            </div>
            {Session.getPackage ? (
              <>
                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoPackageId}: &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Session.packageId}
                  </MDTypography>
                  &nbsp; &nbsp;
                  <button
                    className="btn btn-outline-dark ml-2"
                    onClick={() => {
                      navigate("/Packages/Details/", {
                        state: { pkg: { id: Session.packageId } },
                      });
                    }}
                  >
                    {translations.AccountingSessionInfoGotoPackage}
                  </button>
                </div>

                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoPackageName}: &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Session.getPackage.packageName}
                  </MDTypography>
                </div>
                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoPackageOriginalPrice}:
                    &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Session.getPackage.originalPrice}
                  </MDTypography>
                </div>

                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoDiscount}: &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Number(Session.getPackage.discount).toFixed(1)}%
                  </MDTypography>
                </div>

                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoPackageFinalPrice}:
                    &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Session.getPackage.finalPrice}
                  </MDTypography>
                </div>

                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoOriginalPrice}: &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Session.originalPrice}
                  </MDTypography>
                </div>

                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoFinalPrice}: &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Session.finalPrice}
                  </MDTypography>
                </div>
              </>
            ) : (
              <>
                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoOriginalPrice}: &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Session.originalPrice}
                  </MDTypography>
                </div>

                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoDiscount}: &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Number(Session.discount).toFixed(1)}%
                  </MDTypography>
                </div>

                <div className="col-md-4">
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    {translations.AccountingSessionInfoFinalPrice}: &nbsp;
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;{Session.finalPrice}
                  </MDTypography>
                </div>
              </>
            )}
            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoIsPaidFor}: &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;{Session.isPaidFor ? "Paid by " + Session.paidBy : "NO"}
              </MDTypography>
            </div>

            <div className="col-md-4">
              {/* <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
                {translations.AccountingSessionInfoIsHalfDuration}: &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;{Session.getSession.isHalfDuration ? "YES" : "NO"}
              </MDTypography> */}
            </div>

            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoSessionRequiredTherapists}:
                &nbsp;
              </MDTypography>

              <MDBox display="flex">
                {Session.getSession.getTherapistTypes &&
                  Session.getSession.getTherapistTypes.map((tt, index) => {
                    return (
                      <MDTypography
                        key={index}
                        variant="button"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        {tt.getTherapistType.typeName} &nbsp; &nbsp;Quantity
                        Needed: {tt.quantityNeeded}
                      </MDTypography>
                    );
                  })}
              </MDBox>
            </div>

            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoSessionRequiredFacilities}:
                &nbsp;
              </MDTypography>
              <MDBox display="flex">
                {Session.getSession.getFacilities &&
                  Session.getSession.getFacilities.map((ft, index) => {
                    return (
                      <MDTypography
                        key={index}
                        variant="button"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        {ft.getFacility.typeName} &nbsp; &nbsp;Quantity Needed:{" "}
                        {ft.quantityNeeded}
                      </MDTypography>
                    );
                  })}
              </MDBox>
            </div>
            {!Session.getDomesticStay && !Session.getDomesticStayExtra && (
              <div className="col-md-4">
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  {translations.AccountingSessionInfoPay}: &nbsp;
                </MDTypography>
                {GetPaymentButton()}
              </div>
            )}
            <div className="col-md-4">{GetAppointmentData()}</div>

            <div className="col-md-4">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {translations.AccountingSessionInfoAddmission}:
              </MDTypography>
              <AddmissionButton AccSession={Session} />
            </div>

            {/*     <div className="col-md-4">
              {Session.isAbsent ? (
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => ToggleAbsentButtonClick()}
                  disabled={!Session.isPaidFor}
                >
                  <Icon>undo</Icon>
                  &nbsp;
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;
                    {translations.AccountingSessionInfoUndoAbsent}
                  </MDTypography>
                </button>
              ) : (
                <button
                  className="btn btn-outline-warning mt-2 ml-2"
                  onClick={() => ToggleAbsentButtonClick()}
                  disabled={!Session.isPaidFor}
                >
                  <Icon>check_circle_outline</Icon>
                  &nbsp;
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    &nbsp;
                    {translations.AccountingSessionInfoMakeAbsent}
                  </MDTypography>
                </button>
              )}
            </div> */}

            {["Admin", "Owner", "HeadAccounting"].includes(
              CurrentUser.userType,
            ) && (
              <div className="col-md-4">
                {Session.isException ? (
                  <button
                    className="btn btn-outline-success mt-2 ml-2"
                    onClick={() => ToggleExceptionButtonClick()}
                    disabled={Session.isPaidFor}
                  >
                    <Icon>undo</Icon>
                    &nbsp;
                    <MDTypography
                      variant="button"
                      fontWeight="regular"
                      color="text"
                    >
                      &nbsp;
                      {translations.AccountingSessionInfoRemoveException}
                    </MDTypography>
                  </button>
                ) : (
                  <button
                    className="btn btn-outline-danger mt-2 ml-2"
                    onClick={() => ToggleExceptionButtonClick()}
                    disabled={Session.isPaidFor}
                  >
                    <Icon>check_circle_outline</Icon>
                    &nbsp;
                    <MDTypography
                      variant="button"
                      fontWeight="regular"
                      color="text"
                    >
                      &nbsp;
                      {translations.AccountingSessionInfoMakeEXCEPTION}
                    </MDTypography>
                  </button>
                )}
              </div>
            )}
            <div
              className="col-md-4  mt-3 "
              sx={{
                backgroundColor: "#3560AD",
                "&:hover": { backgroundColor: "#4682B4" },
              }}
            >
              <a
                href={redirectLink}
                className="btn btn-primary"
                rel="noreferrer"
              >
                {translations.AccountingSessionInfomakeFeedback}
              </a>
            </div>
            <div className="col-md-4 mt-3">
              {receipt && <GenerateReceiptButton receiptId={receipt} />}
            </div>
          </div>
        </div>
      </MDBox>
    </Card>
  );
};

export default AccSessionDetailsCard;
